import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import logoAlbaker from "@/assets/logo-albaker.png";

const Articles = () => {
  const { lang, isRTL } = useLanguage();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/articles", { params: { limit: 50 } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const t = lang === "ar"
    ? { back: "الرئيسية", overline: "المقالات", heading: "أحداث · مناسبات · فيديوهات", empty: "لا توجد مقالات منشورة بعد." }
    : { back: "Home", overline: "Articles", heading: "Events · Occasions · Videos", empty: "No published articles yet." };

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="min-h-screen bg-ink text-bone">
      <div className="container-x py-20">
        <div className="mb-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="articles-back">
            <img src={logoAlbaker} alt="" className="h-10 w-auto" />
            <span className="overline">{t.back}</span>
          </Link>
        </div>

        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="hairline" />
            <span className="overline">{t.overline}</span>
          </div>
          <h1 className={`${isRTL ? "font-arabic font-black leading-[1.15]" : "font-serif leading-[1.05]"} text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight`}>
            {t.heading}
          </h1>
        </div>

        {loading && <div className="text-white/50 overline animate-pulse">…</div>}
        {error && <div className="text-red-400">{error}</div>}
        {!loading && items.length === 0 && (
          <div className="text-white/50 font-arabic">{t.empty}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {items.map((a) => {
            const localized = a[lang] || a.ar || a.en || {};
            return (
              <Link
                to={`/articles/${a.slug}`}
                key={a.id}
                data-testid={`article-link-${a.slug}`}
                className="group relative block border border-white/10 hover:border-gold/40 transition-all duration-500 min-h-[300px] overflow-hidden"
              >
                {a.cover_image ? (
                  <img src={a.cover_image} alt={localized.title || ""} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                ) : (
                  <div className="absolute inset-0 bg-ink-soft" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/20" />
                <div className="relative z-10 h-full p-8 lg:p-10 flex flex-col justify-end min-h-[300px]">
                  <div className="text-[11px] tracking-[0.25em] uppercase text-white/50 mb-3">
                    {(a.publish_date || "").slice(0, 10)}
                  </div>
                  <h3 className={`${isRTL ? "font-arabic font-bold" : "font-serif"} text-bone text-2xl sm:text-3xl leading-tight group-hover:text-gold transition-colors`}>
                    {localized.title || "—"}
                  </h3>
                  {localized.excerpt && (
                    <p className={`mt-3 text-white/60 ${isRTL ? "font-arabic text-base" : "text-sm"} leading-relaxed`}>
                      {localized.excerpt}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Articles;
